/**
 * Post Loader Utilities
 * 
 * Handles loading and parsing of blog posts from the content directory.
 */

import { BlogConfig } from '../BlogConfig';
import { parseFrontmatter } from './frontmatterParser';
// Import the posts index
import { loadAllPosts as loadPostFiles, loadPostContent } from '../../content/posts';

/**
 * Parse frontmatter and content from a post file
 */
export const parsePost = (fileId, fileContent, locale = 'en-US') => {
  const { data, content } = parseFrontmatter(fileContent);
  
  // Ensure date is a valid Date object
  let postDate = new Date();
  if (data.date) {
    const parsedDate = new Date(data.date);
    if (!isNaN(parsedDate.getTime())) {
      postDate = parsedDate;
    }
  }
  
  return {
    slug: fileId,
    title: data.title || 'Untitled Post',
    description: data.description || '',
    date: postDate,
    formattedDate: data.date 
      ? postDate.toLocaleDateString(locale, BlogConfig.dateOptions)
      : '',
    category: data.category || 'uncategorized',
    tags: data.tags || [],
    author: data.author || BlogConfig.author,
    thumbnail: data.thumbnail || BlogConfig.content.defaultThumbnail || null,
    readingTime: calculateReadingTime(content),
    featured: data.featured || false,
    draft: data.draft || false,
    content,
    ...data, // Include any additional frontmatter fields
  };
};

/**
 * Calculate estimated reading time
 */
export const calculateReadingTime = (content) => {
  const wordsPerMinute = 200;
  const words = content.trim().split(/\s+/).length;
  const minutes = Math.ceil(words / wordsPerMinute);
  return minutes;
};

/**
 * Load all posts
 */
export const loadAllPosts = async (locale = 'en-US') => {
  const postFiles = await loadPostFiles();
  const posts = [];
  
  for (const postFile of postFiles) {
    try {
      const content = postFile.content;
      
      if (typeof content === 'string') {
        const post = parsePost(postFile.id, content, locale);
        if (!post.draft) {
          posts.push(post);
        }
      }
    } catch (error) {
      console.error(`Error loading post ${postFile.id}:`, error);
    }
  }
  
  // Sort by date (newest first)
  return posts.sort((a, b) => b.date - a.date);
};

/**
 * Load a single post by slug
 */
export const loadPostBySlug = async (slug, locale = 'en-US') => {
  const content = await loadPostContent(slug);
  if (!content) return null;
  
  const post = parsePost(slug, content, locale);
  return post.draft ? null : post;
};

/**
 * Load posts by category
 */
export const loadPostsByCategory = async (category) => {
  const posts = await loadAllPosts();
  return posts.filter(post => post.category === category);
};

/**
 * Load posts by tag
 */
export const loadPostsByTag = async (tag) => {
  const posts = await loadAllPosts();
  return posts.filter(post => post.tags.includes(tag));
};

/**
 * Get all unique categories
 */
export const getAllCategories = async () => {
  const posts = await loadAllPosts();
  const categories = new Set(posts.map(post => post.category));
  return Array.from(categories);
};

/**
 * Get all unique tags
 */
export const getAllTags = async () => {
  const posts = await loadAllPosts();
  const tags = new Set(posts.flatMap(post => post.tags));
  return Array.from(tags);
};

/**
 * Search posts by query
 */
export const searchPosts = async (query) => {
  if (!query || query.trim() === '') return [];
  
  const posts = await loadAllPosts();
  const searchTerms = query.toLowerCase().split(' ');
  
  return posts.filter(post => {
    const searchContent = `${post.title} ${post.description} ${post.tags.join(' ')} ${post.content}`.toLowerCase();
    return searchTerms.every(term => searchContent.includes(term));
  });
};

/**
 * Get featured posts
 */
export const getFeaturedPosts = async (limit = 3, locale = 'en-US') => {
  const posts = await loadAllPosts(locale);
  return posts.filter(post => post.featured).slice(0, limit);
};

// Named export for default
const postLoader = {
  loadAllPosts,
  loadPostBySlug,
  loadPostsByCategory,
  loadPostsByTag,
  getAllCategories,
  getAllTags,
  searchPosts,
  getFeaturedPosts,
};

export default postLoader;
